<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import TableHeaderCell from 'dashboard/components/widgets/TableHeaderCell.vue';
import CheckBox from 'v3/components/Form/CheckBox.vue';
import {
  hasPushPermissions,
  requestPushPermissions,
  verifyServiceWorkerExistence,
  PUSH_PERMISSION_ERRORS,
} from 'dashboard/helper/pushHelper.js';
import { setBrowserPushSubscribed } from 'dashboard/helper/notificationPreferences';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import { NOTIFICATION_TYPES } from './constants';

const ensureMovementReasonPreferenceScript = () => {
  if (typeof window.__aclGetMovementReasonPreference === 'function') {
    return Promise.resolve();
  }

  return new Promise(resolve => {
    const existingScript = document.querySelector(
      'script[data-aceleres-movement-reason-preference]'
    );
    if (existingScript) {
      existingScript.addEventListener('load', resolve, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src =
      '/aceleres-funnel-movement-reason-preference.js?v=20260803-funnel-regression-v1';
    script.dataset.aceleresMovementReasonPreference = 'true';
    script.addEventListener('load', resolve, { once: true });
    script.addEventListener('error', resolve, { once: true });
    document.head.appendChild(script);
  });
};

export default {
  components: {
    TableHeaderCell,
    ToggleSwitch,
    CheckBox,
  },
  data() {
    return {
      selectedEmailFlags: [],
      selectedPushFlags: [],
      enableAudioAlerts: false,
      hasEnabledPushPermissions: false,
      // Identifica o clique mais recente; respostas de cliques anteriores são
      // descartadas em vez de sobrescrever o estado atual da chave.
      pushRequestId: 0,
      movementReasonsEnabled: false,
      movementReasonsTitle: 'Desativar Motivos de Movimentação',
      movementReasonsDescription:
        'Funcionalidade Temporaria, Quando a opção estiver Ativada, mover o lead é concluido direto, sem abrir o modal de Motivos.',
      movementReasonsCanManage: false,
      notificationTypes: NOTIFICATION_TYPES,
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      emailFlags: 'userNotificationSettings/getSelectedEmailFlags',
      pushFlags: 'userNotificationSettings/getSelectedPushFlags',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      inboxPreferences: 'inboxNotificationPreferences/getInboxPreferences',
    }),
    hasPushAPISupport() {
      return !!('Notification' in window);
    },
    isSLAEnabled() {
      return this.isFeatureEnabledonAccount(this.accountId, FEATURE_FLAGS.SLA);
    },
    filteredNotificationTypes() {
      return this.notificationTypes.filter(notification =>
        this.isSLAEnabled
          ? true
          : ![
              'sla_missed_first_response',
              'sla_missed_next_response',
              'sla_missed_resolution',
            ].includes(notification.value)
      );
    },
  },
  watch: {
    emailFlags(value) {
      this.selectedEmailFlags = value;
    },
    pushFlags(value) {
      this.selectedPushFlags = value;
    },
  },
  mounted() {
    if (hasPushPermissions()) {
      this.getPushSubscription();
    }
    this.$store.dispatch('userNotificationSettings/get');
    this.$store.dispatch('inboxNotificationPreferences/get');
    ensureMovementReasonPreferenceScript().then(() => {
      this.loadMovementReasonPreference();
    });
    window.addEventListener(
      'acl:movement-reason-preference-api-ready',
      this.loadMovementReasonPreference
    );
  },
  beforeUnmount() {
    window.removeEventListener(
      'acl:movement-reason-preference-api-ready',
      this.loadMovementReasonPreference
    );
  },
  methods: {
    async loadMovementReasonPreference() {
      const getPreference = window.__aclGetMovementReasonPreference;
      if (typeof getPreference !== 'function') return;

      try {
        const response = await getPreference();
        const data = response?.data || {};
        this.movementReasonsEnabled = data.enabled === true;
        this.movementReasonsCanManage = data.can_manage === true;
      } catch (error) {
        this.movementReasonsCanManage = false;
      }
    },
    async toggleMovementReasonPreference() {
      const updatePreference = window.__aclUpdateMovementReasonPreference;
      if (typeof updatePreference !== 'function') return;

      const requestedValue = this.movementReasonsEnabled;
      try {
        const response = await updatePreference(requestedValue);
        this.movementReasonsEnabled = response?.data?.enabled === true;
      } catch (error) {
        this.movementReasonsEnabled = !requestedValue;
        useAlert('Não foi possível salvar a preferência.');
      }
    },
    checkFlagStatus(type, flagType) {
      const selectedFlags =
        type === 'email' ? this.selectedEmailFlags : this.selectedPushFlags;
      return selectedFlags.includes(`${type}_${flagType}`);
    },
    // Registrar/cancelar leva cerca de um segundo. Sem este controle a resposta
    // de um clique antigo chega depois do clique seguinte e sobrescreve a
    // intenção mais recente — a chave trava ligada por dentro e cinza na tela,
    // e a partir daí todo clique passa a mandar desligar.
    isStalePushResponse(requestId) {
      return requestId !== this.pushRequestId;
    },
    onRegistrationSuccess(requestId) {
      if (this.isStalePushResponse(requestId)) return;
      this.hasEnabledPushPermissions = true;
      // A chave vale imediatamente para os alertas locais, sem recarregar.
      setBrowserPushSubscribed(true);
    },
    onRequestPermissions(value) {
      const requestId = this.pushRequestId + 1;
      this.pushRequestId = requestId;

      if (!value) {
        this.disablePushPermissions(requestId);
        return;
      }

      requestPushPermissions({
        onSuccess: () => this.onRegistrationSuccess(requestId),
        onError: reason => this.onRegistrationError(reason, requestId),
        onPrompt: () => this.onPermissionPrompt(requestId),
      });
    },
    onPermissionPrompt(requestId) {
      if (this.isStalePushResponse(requestId)) return;
      useAlert(
        this.$t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.PERMISSION_ERRORS.PENDING')
      );
    },
    onRegistrationError(reason, requestId) {
      if (this.isStalePushResponse(requestId)) return;
      // Sem inscrição criada a chave não pode continuar ligada, senão a tela
      // passa a informação errada e o usuário fica sem saber o que aconteceu.
      this.hasEnabledPushPermissions = false;
      setBrowserPushSubscribed(false);

      const prefix = 'PROFILE_SETTINGS.FORM.NOTIFICATIONS.PERMISSION_ERRORS';
      const messages = {
        [PUSH_PERMISSION_ERRORS.BLOCKED]: `${prefix}.BLOCKED`,
        [PUSH_PERMISSION_ERRORS.DISMISSED]: `${prefix}.DISMISSED`,
        [PUSH_PERMISSION_ERRORS.UNSUPPORTED]: `${prefix}.UNSUPPORTED`,
        [PUSH_PERMISSION_ERRORS.SERVICE_WORKER_FAILED]: `${prefix}.SERVICE_WORKER`,
        [PUSH_PERMISSION_ERRORS.MISSING_VAPID_KEY]: `${prefix}.MISSING_VAPID_KEY`,
      };

      useAlert(this.$t(messages[reason] || `${prefix}.GENERIC`));
    },
    disablePushPermissions(requestId) {
      verifyServiceWorkerExistence(registration =>
        registration.pushManager
          .getSubscription()
          .then(subscription => {
            if (subscription) {
              return subscription.unsubscribe();
            }
            return null;
          })
          .finally(() => {
            if (this.isStalePushResponse(requestId)) return;
            this.hasEnabledPushPermissions = false;
            setBrowserPushSubscribed(false);
          })
          .catch(() => {})
      );
    },
    getPushSubscription() {
      verifyServiceWorkerExistence(registration =>
        registration.pushManager
          .getSubscription()
          .then(subscription => {
            // A inscrição do navegador é a fonte da verdade: a chave passa a
            // refletir o que existe de fato, não o que a tela supunha.
            this.hasEnabledPushPermissions = !!subscription;
            setBrowserPushSubscribed(!!subscription);
          })
          // eslint-disable-next-line no-console
          .catch(error => console.log(error))
      );
    },
    // Sem `await` a promise rejeitada nunca chegava ao catch: a tela avisava
    // "salvo com sucesso" mesmo quando o servidor recusava (401, por exemplo)
    // e nada era gravado. Devolve se a gravação realmente aconteceu.
    async updateNotificationSettings() {
      try {
        await this.$store.dispatch('userNotificationSettings/update', {
          selectedEmailFlags: this.selectedEmailFlags,
          selectedPushFlags: this.selectedPushFlags,
        });
        useAlert(this.$t('PROFILE_SETTINGS.FORM.API.UPDATE_SUCCESS'));
        return true;
      } catch (error) {
        useAlert(this.$t('PROFILE_SETTINGS.FORM.API.UPDATE_ERROR'));
        return false;
      }
    },
    handleInput(type, id) {
      if (type === 'email') {
        this.handleEmailInput(id);
      } else {
        this.handlePushInput(id);
      }
    },
    // A marcação volta ao estado anterior quando a gravação falha, para a tela
    // não continuar exibindo uma preferência que o servidor não tem.
    async handleEmailInput(id) {
      const previousFlags = this.selectedEmailFlags;
      this.selectedEmailFlags = this.toggleInput(this.selectedEmailFlags, id);
      const saved = await this.updateNotificationSettings();
      if (!saved) this.selectedEmailFlags = previousFlags;
    },
    async handlePushInput(id) {
      const previousFlags = this.selectedPushFlags;
      this.selectedPushFlags = this.toggleInput(this.selectedPushFlags, id);
      const saved = await this.updateNotificationSettings();
      if (!saved) this.selectedPushFlags = previousFlags;
    },
    toggleInput(selected, current) {
      if (selected.includes(current)) {
        const newSelectedFlags = selected.filter(flag => flag !== current);
        return newSelectedFlags;
      }
      return [...selected, current];
    },
    async toggleInboxPreference(inboxId, currentValue) {
      const newPreferences = {};
      (this.inboxPreferences || []).forEach(item => {
        newPreferences[item.id] =
          item.id === inboxId ? !currentValue : item.enabled;
      });
      try {
        await this.$store.dispatch(
          'inboxNotificationPreferences/update',
          newPreferences
        );
        useAlert(
          this.$t(
            'PROFILE_SETTINGS.FORM.NOTIFICATIONS.INBOX_FILTER.SAVE_SUCCESS'
          )
        );
      } catch (error) {
        useAlert(
          this.$t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.INBOX_FILTER.SAVE_ERROR')
        );
      }
    },
  },
};
</script>

<template>
  <div id="profile-settings-notifications" class="flex flex-col gap-6">
    <!-- Layout for desktop devices -->
    <div class="hidden sm:block">
      <div
        class="grid content-center h-12 grid-cols-12 gap-4 py-0 rounded-t-xl"
      >
        <TableHeaderCell
          :span="7"
          label="`${$t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.TYPE_TITLE')}`"
        >
          <span class="text-heading-3 normal-case text-n-slate-12">
            {{ $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.TYPE_TITLE') }}
          </span>
        </TableHeaderCell>
        <TableHeaderCell
          :span="2"
          label="`${$t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.EMAIL')}`"
        >
          <span class="text-heading-3 normal-case text-n-slate-12">
            {{ $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.EMAIL') }}
          </span>
        </TableHeaderCell>
        <TableHeaderCell
          :span="3"
          label="`${$t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.PUSH')}`"
        >
          <div class="flex items-center justify-between gap-1">
            <span
              class="text-heading-3 normal-case text-n-slate-12 whitespace-nowrap"
            >
              {{ $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.PUSH') }}
            </span>
          </div>
        </TableHeaderCell>
      </div>
      <div
        v-for="(notification, index) in filteredNotificationTypes"
        :key="index"
      >
        <div
          class="grid items-center content-center h-12 grid-cols-12 gap-4 py-0 rounded-t-xl"
        >
          <div
            class="flex flex-row items-start gap-2 col-span-7 px-0 py-2 text-sm tracking-[0.5] rtl:text-right"
          >
            <span class="text-body-main text-n-slate-12">
              {{ $t(notification.label) }}
            </span>
          </div>
          <div
            v-for="(type, typeIndex) in ['email', 'push']"
            :key="typeIndex"
            class="flex items-start gap-2 px-0 text-sm tracking-[0.5] text-left rtl:text-right"
            :class="`col-span-${type === 'push' ? 3 : 2}`"
          >
            <CheckBox
              :value="`${type}_${notification.value}`"
              :is-checked="
                checkFlagStatus(type, notification.value, selectedPushFlags)
              "
              @update="id => handleInput(type, id)"
            />
          </div>
        </div>
      </div>
    </div>
    <!--  Layout for mobile devices -->
    <div class="flex flex-col gap-6 sm:hidden">
      <span class="text-heading-3 text-n-slate-12">
        {{ $t('PROFILE_SETTINGS.FORM.EMAIL_NOTIFICATIONS_SECTION.TITLE') }}
      </span>
      <div class="flex flex-col gap-4">
        <div
          v-for="(notification, index) in filteredNotificationTypes"
          :key="index"
          class="flex flex-row items-start gap-2"
        >
          <CheckBox
            :id="`email_${notification.value}`"
            :value="`email_${notification.value}`"
            :is-checked="checkFlagStatus('email', notification.value)"
            @update="handleEmailInput"
          />
          <span class="text-body-main text-n-slate-12">{{
            $t(notification.label)
          }}</span>
        </div>
      </div>

      <div class="flex items-center justify-start gap-2">
        <span class="text-heading-3 text-n-slate-12">
          {{ $t('PROFILE_SETTINGS.FORM.PUSH_NOTIFICATIONS_SECTION.TITLE') }}
        </span>
      </div>

      <div class="flex flex-col gap-4">
        <div
          v-for="(notification, index) in filteredNotificationTypes"
          :key="index"
          class="flex flex-row items-start gap-2"
        >
          <CheckBox
            :id="`push_${notification.value}`"
            :value="`push_${notification.value}`"
            :is-checked="checkFlagStatus('push', notification.value)"
            @update="handlePushInput"
          />
          <span class="text-body-main text-n-slate-12">{{
            $t(notification.label)
          }}</span>
        </div>
      </div>
    </div>

    <div
      class="flex items-center justify-between w-full gap-2 p-4 border border-solid border-n-weak rounded-xl"
    >
      <div class="flex flex-row items-center gap-2">
        <fluent-icon
          icon="alert"
          class="flex-shrink-0 text-n-slate-12"
          size="18"
        />
        <span class="text-body-main text-n-slate-12">
          {{ $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.BROWSER_PERMISSION') }}
        </span>
      </div>
      <ToggleSwitch
        v-model="hasEnabledPushPermissions"
        @change="onRequestPermissions"
      />
    </div>

    <div
      v-if="movementReasonsCanManage"
      class="flex flex-col w-full gap-4 p-4 border border-solid border-n-weak rounded-xl"
    >
      <div class="flex flex-col gap-1">
        <div class="flex flex-row items-center gap-2">
          <fluent-icon
            icon="alert"
            class="flex-shrink-0 text-n-slate-12"
            size="18"
          />
          <span class="text-heading-3 text-n-slate-12">
            {{ movementReasonsTitle }}
          </span>
        </div>
        <span class="text-body-sm text-n-slate-11">
          {{ movementReasonsDescription }}
        </span>
      </div>
      <div class="flex justify-end">
        <ToggleSwitch
          v-model="movementReasonsEnabled"
          @change="toggleMovementReasonPreference"
        />
      </div>
    </div>

    <!-- Inbox channel filter section -->
    <div class="flex flex-col gap-4 pt-2">
      <div>
        <span class="text-heading-3 text-n-slate-12 block mb-1">
          {{ $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.INBOX_FILTER.TITLE') }}
        </span>
        <span class="text-body-sm text-n-slate-11">
          {{
            $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.INBOX_FILTER.DESCRIPTION')
          }}
        </span>
      </div>
      <div
        v-for="inbox in inboxPreferences"
        :key="inbox.id"
        class="flex items-center justify-between w-full gap-2 p-4 border border-solid border-n-weak rounded-xl"
      >
        <div class="flex flex-row items-center gap-2">
          <fluent-icon
            icon="channel"
            class="flex-shrink-0 text-n-slate-12"
            size="18"
          />
          <span class="text-body-main text-n-slate-12">{{ inbox.name }}</span>
        </div>
        <ToggleSwitch
          :model-value="inbox.enabled"
          @change="toggleInboxPreference(inbox.id, inbox.enabled)"
        />
      </div>
    </div>
  </div>
</template>
